import type {
  BtlAddress,
  BtlApplication,
  ResidenceRegion,
  UpsertIntermediaryFeeDto,
  UpdateCaseDto,
} from 'api/btlApplicationForm/types';
import { PropertyTenure, RefinancePurpose } from 'api/btlApplicationForm/types';
import type { ExistingFirmResponse } from 'api/firm/firm.types';
import { mapFormSubmissionFunnelTypeToApi } from 'pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import { transformAddress, hasCompleteAddressEntry } from '../common/Address';
import { buildIntroducerPayloadFromFormValues } from './btlIntroducerHelpers';
import { calculateDepositAmount, normalizeDepositSourcesForSubmit } from './depositSourcesHelpers';

interface BuildUpdateFinanceAndPropertyDtoParams {
  caseId: string;
  propertyId?: string;
  mergedValues: Record<string, any>;
  data?: BtlApplication;
  firm?: ExistingFirmResponse | null;
  includeDepositSources?: boolean;
}

function normalizeFeesAddedToBalance(value: unknown, fallback: boolean): boolean {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
}

function normalizeTermInMonths(value: unknown, fallbackMonths: number): number {
  if (typeof value !== 'number') {
    return fallbackMonths;
  }

  // Hydrated form values are in years; submitted form values are transformed to months.
  if (value <= 40) {
    return value * 12;
  }

  return value;
}

function normalizeCheckboxValue(value: unknown): boolean | null | undefined {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return undefined;
}

/**
 * Backend accepts `address: null` for EXPECTED properties and preserves the existing
 * address in the domain model — see strideup-be case.e2e.ts finance-and-property tests.
 */
function resolvePropertyAddress(mergedValues: Record<string, any>): BtlAddress | null {
  if (!hasCompleteAddressEntry(mergedValues)) {
    return null;
  }

  return transformAddress(mergedValues, true);
}

function omitUndefined<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)) as Partial<T>;
}

export function normalizeIntermediaryFees(
  hasIntermediaryFees: unknown,
  fees: unknown,
): UpsertIntermediaryFeeDto[] | undefined {
  if (hasIntermediaryFees === undefined && fees === undefined) {
    return undefined;
  }

  if (hasIntermediaryFees !== 'true') {
    return [];
  }

  if (!Array.isArray(fees)) {
    return [];
  }

  return fees.map(fee => {
    const normalized: UpsertIntermediaryFeeDto = {
      description: String(fee.description ?? ''),
      amount: Number(fee.amount) || 0,
      whenPayable: String(fee.whenPayable ?? ''),
      howPayable: String(fee.howPayable ?? ''),
      refundableAmount: Number(fee.refundableAmount) || 0,
    };

    if (typeof fee.id === 'string' && fee.id) {
      normalized.id = fee.id;
    }

    return normalized;
  });
}

export function buildUpdateFinanceAndPropertyDto({
  caseId,
  propertyId,
  mergedValues,
  data,
  firm,
  includeDepositSources = false,
}: BuildUpdateFinanceAndPropertyDtoParams): UpdateCaseDto {
  const existingProperty = data?.applicationProperty?.property;

  const purpose = mergedValues.purpose ?? data?.purpose;
  const refinancePurpose =
    purpose === 'PURCHASE' ? null : mergedValues.refinancePurpose ?? data?.refinancePurpose ?? null;
  const financingAmount = mergedValues.financingAmount ?? data?.financingAmount;
  const paymentProfile = mergedValues.paymentProfile ?? data?.paymentProfile ?? null;
  const resolvedPropertyType = mergedValues.propertyType ?? existingProperty?.propertyType;
  const resolvedHasCommunalSpace =
    normalizeCheckboxValue(mergedValues.hasCommunalSpace) ?? existingProperty?.attributes?.hasCommunalSpace ?? null;

  const outstandingBalance =
    refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE
      ? financingAmount
      : mergedValues.outstandingBalance ?? data?.outstandingBalance ?? null;

  const purchasePrice = mergedValues.purchasePrice ?? existingProperty?.purchasePrice ?? 0;
  const property = omitUndefined({
    purchasePrice,
    grossMonthlyRentalPayment:
      mergedValues.grossMonthlyRentalPayment ?? existingProperty?.grossMonthlyRentalPayment ?? 0,
    address: resolvePropertyAddress(mergedValues),
    propertyType: resolvedPropertyType || undefined,
    propertySubType: mergedValues.propertySubType ?? existingProperty?.propertySubType ?? undefined,
    expectedPropertyType: mergedValues.expectedPropertyType ?? existingProperty?.expectedPropertyType ?? undefined,
    region: (mergedValues.region ?? existingProperty?.region) as ResidenceRegion | null | undefined,
    attributes:
      resolvedPropertyType === 'HMO'
        ? {
            numberOfBedrooms: mergedValues.numberOfBedrooms ?? existingProperty?.attributes?.numberOfBedrooms ?? null,
            hasCommunalSpace: resolvedHasCommunalSpace,
          }
        : resolvedPropertyType === 'MUB'
        ? { numberOfUnits: mergedValues.numberOfUnits ?? existingProperty?.attributes?.numberOfUnits ?? null }
        : undefined,
  });

  const propertyData = omitUndefined({
    property,
    tenure: mergedValues.tenure as PropertyTenure | undefined,
    yearsRemainingOnLease: mergedValues.yearsRemainingOnLease,
    estateAgent: mergedValues.estateAgent,
  });

  const submissionFunnelType = mapFormSubmissionFunnelTypeToApi(mergedValues.submissionFunnelType);
  const introducer = firm ? buildIntroducerPayloadFromFormValues(mergedValues, firm) : undefined;
  const intermediaryFees = normalizeIntermediaryFees(mergedValues.hasIntermediaryFees, mergedValues.fees);

  const isPurchase = purpose === 'PURCHASE';
  const depositAmount = isPurchase
    ? calculateDepositAmount(purchasePrice, financingAmount)
    : data?.depositAmount ?? null;
  const depositSources =
    isPurchase && includeDepositSources ? normalizeDepositSourcesForSubmit(mergedValues.depositSources) : undefined;

  return omitUndefined({
    caseId,
    propertyId: propertyId ?? existingProperty?.id,
    financeData: {
      purpose,
      refinancePurpose,
      feesAddedToBalance: normalizeFeesAddedToBalance(
        mergedValues.feesAddedToBalance,
        data?.feesAddedToBalance ?? false,
      ),
      productId: data?.product?.id ?? null,
      termInMonths: normalizeTermInMonths(mergedValues.termInMonths, data?.termInMonths ?? 0),
      depositAmount,
      ...(depositSources !== undefined ? { depositSources } : {}),
      financingAmount,
      outstandingBalance: isPurchase ? null : outstandingBalance,
      paymentProfile,
      repaymentStrategy: mergedValues.repaymentStrategy ?? null,
      repaymentStrategyDetails: mergedValues.repaymentStrategyDetails ?? null,
    },
    propertyData,
    submissionFunnelType,
    introducer,
    ...(intermediaryFees !== undefined ? { intermediaryFees } : {}),
  }) as UpdateCaseDto;
}
